import { forwardRef, Input as BaseInput, InputProps } from '@chakra-ui/react';
import React, { ChangeEvent, useState, useEffect, FocusEvent } from 'react';

interface Props extends Omit<InputProps, 'variant'> {
  kind?: 'number' | 'text';
  variant?: InputProps['variant'] | 'small';
}

const Input = forwardRef<Props, 'input'>(({ kind = 'text', onChange, onBlur, min, variant, defaultValue, ...props }, ref) => {
  const [value, setValue] = useState<string | number>(defaultValue ? (kind === 'number' ? (defaultValue as number) : (defaultValue as string)) : '');

  useEffect(() => {
    if (min || min === 0) {
      setValue(min);
    }
  }, [min]);

  function maskInput(newValue: string): string | number {
    if (kind === 'number') {
      const numberValue = Number(newValue);
      const lastChar = newValue.charAt(newValue.length - 1);
      let result = Number.isNaN(numberValue) ? value : numberValue;

      if (newValue === '') {
        result = '';
      } else if (lastChar === '.' || lastChar === ',') {
        result = `${result}${lastChar}`;
      }

      setValue(result);
      return result;
    }
    setValue(newValue);
    return newValue;
  }

  const maxPermissibleInteger = 2147483647;

  const limitInputNumber = (newValue: number): number => Math.min(newValue, maxPermissibleInteger);

  const processInput = (newValue: string): string | number => {
    const processedValue: number | string = maskInput(newValue);
    return kind === 'number' && typeof processedValue === 'number' ? limitInputNumber(processedValue) : processedValue;
  };

  const handleBlur = (newValue: string): void => {
    if ((min && Number(newValue) < min) || min === 0) {
      setValue(min);
    }
  };

  return (
    <BaseInput
      ref={ref}
      value={value}
      {...props}
      variant={variant}
      onChange={(e: ChangeEvent<HTMLInputElement>): void => {
        // @ts-ignore
        e.target.value = processInput(e.target.value);
        onChange?.(e);
      }}
      onBlur={(e: FocusEvent<HTMLInputElement>): void => {
        handleBlur(e.target.value);
        onBlur && onBlur(e);
      }}
    />
  );
});

export default Input;
